import {
  useCallback,
  useMemo,
  useReducer,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'motion/react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@reflex/ui/components/ui/dialog';
import { Button } from '@reflex/ui/components/ui/button';
import { InlineDocsHint } from '@reflex/ui/components/layout/Typography';
import { KbdCombo } from '@reflex/ui/components/shared/Kbd';
import { AnimatedCollapse } from '@reflex/ui/components/shared/AnimatedCollapse';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { ChatInput } from '@reflex/plugin-chat/web/components/ChatInput';
import { useAgentTypeRegistry } from '@/lib/agent-type-registry';
import { useStartAgent } from '@/hooks/agents/useAgents';
import { useBlueprints } from '@/hooks/blueprints/useBlueprints';
import { useSnapshots } from '@/hooks/snapshots/useSnapshots';
import { useHasCapability } from '@/hooks/auth/useHasCapability';
import { useAvailableBlueprints } from '@/hooks/blueprints/useAvailableBlueprints';
import { useBaseBlueprintBuilding } from '@/hooks/blueprints/useBaseBlueprintBuilding';
import { resolveLaunchBlueprintName } from '@/hooks/blueprints/resolveLaunchBlueprintName';
import { resolveEffectiveSandboxSize } from '@/components/agent/blueprint-size';
import { BlueprintBuildingNotice } from '@/components/blueprint/BlueprintBuildingNotice';
import {
  useAgentPersonas,
  agentPersonasKey,
} from '@reflex/plugin-agent-persona/web/useAgentPersonas';
import { useQueryClient } from '@tanstack/react-query';
import { useOrgKey } from '@reflex/ui/client/context';
import { useAgentModelSupportHelpers } from '@/hooks/config/useAgentModelSupport';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { ClipboardList, Eye, Rocket, RotateCcw, X } from 'lucide-react';
import { useSandboxHealth } from '@/hooks/org/useSandboxHealth';
import {
  buildContentBlocks,
  GIT_REPO_ATTACHMENT_ID,
  MAX_ATTACHMENTS_TOTAL_BYTES,
  resolveSandboxOptions,
} from '@runloop/reflex-contract';
import {
  DEVBOX_SNAPSHOTS_CAPABILITY,
  MAX_TRIAL_RESOURCE_SIZE,
  resourceGrantRoleId,
  defaultSessionShareAccess,
  effectiveDefaultSessionShare,
  isResourceSizeAllowedForTrial,
  isSessionSharingDisabled,
} from '@reflex/shared';
import type { GitRepoAttachmentConfig, PluginAttachmentValue } from '@runloop/reflex-contract';
import type { ModelProviderSecretType } from '@reflex/shared';
import { useResolveGitHubPr } from '@reflex/plugin-github/web/useGitHubRepos';
import { toModelProvider } from '@/components/agent/to-model-provider';
import { useAccessibleModelProviderSecrets } from '@reflex/ui/client/hooks';
import { isEndpointSatisfiableByProviderKey } from './endpoint-key-eligibility';
import { promptPullRequest } from './prompt-pull-request';
import type { Agent, AgentType, Attachment } from '@/lib/types';
import type { AgentPersona } from '@reflex/plugin-agent-persona/shared/types';
import { sanitizeAttachments, sanitizeEnvVars } from '@reflex/ui/client/agent-config-utils';
import { AgentConfigFormSection } from '@/components/agent/AgentConfigFormSection';
import { AgentSettingsChipContainer } from '@/components/agent/AgentSettingsChipContainer';
import {
  launchFormReducer,
  createInitialState,
  mergeAttachmentDefaults,
  readGitRepoAttachment,
  type LaunchAgentDialogDefaults,
  type LaunchFormState,
  type LaunchFormAction,
} from './form-state';
import { saveFormState, loadFormState, clearPersistedFormState } from './persistence';
import {
  draftPayloadToPersistedData,
  isLaunchFormDirty,
  launchFormToDraftPayload,
} from './draft-payload';
import { useAgentDraftAutosave } from '@/hooks/agents/useAgentDraftAutosave';
import { useAgentDrafts } from '@/hooks/agents/useAgentDrafts';
import { DraftAutosaveIndicator } from './DraftAutosaveIndicator';
import { useEffectiveModelEndpoint } from './useEffectiveModelEndpoint';
import { useLaunchAgentDefaults } from './useLaunchAgentDefaults';
import { useLaunchAgentSync } from './useLaunchAgentSync';
import { PersonaDetailCard } from './PersonaDetailCard';
import { LaunchPersonaHeader } from './LaunchPersonaHeader';
import { AppliedContextBar } from './AppliedContextBar';
import { AddContextButton } from './AddContextButton';
import { AgentRepoBlueprintWarning } from '@/components/agent/AgentRepoBlueprintWarning';
import {
  LaunchMentionMenu,
  type LaunchMentionCreateRequest,
  type LaunchMentionEditRequest,
} from './LaunchMentionMenu';
import { resolveMentionApply, type MentionApplyAction } from './mention-apply';
import type { LaunchMentionResult } from '@reflex/plugin-api';
import { useFeatureFlag } from '@/hooks/config/useFeatureFlag';
import { usePlugins } from '@/hooks/config/usePlugins';
import { useAuth } from '@/hooks/auth/useAuth';
import { useAgentGroups } from '@/hooks/agents/useAgentGroups';
import { useMyTeams } from '@/hooks/auth/useMyTeams';
import { useUsers } from '@/hooks/auth/useUsers';
import { asOrgId } from '@reflex/ui/client/api/org-id';
import { useCreateResourceGrantMutation } from '@reflex/ui/client/api/resource-grants';
import { useSessionSharingPolicy } from '@/hooks/auth/useSessionSharingPolicy';
import { LaunchShareMenu, type PendingShare } from './LaunchShareMenu';

export interface LaunchAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (agent: Agent) => void;
  /**
   * Seed values for the form. Used by the "Launch again" action to pre-fill
   * the dialog from an existing agent's configuration so the user can launch
   * a fresh run without re-typing everything.
   */
  defaults?: LaunchAgentDialogDefaults;
  /**
   * One-shot attachments applied on top of remembered launch settings.
   * Resource pages use this to open the launcher with a specific resource
   * already selected without dropping the user's unrelated saved context.
   */
  attachmentDefaults?: Record<string, PluginAttachmentValue>;
  /**
   * One-shot group (`agp_*`) applied on top of remembered launch settings, the
   * same way `attachmentDefaults` is. The sidebar's per-group "+" passes it so
   * a run started from a group heading is filed there — which has to beat the
   * remembered group, since the heading is the more specific intent.
   */
  groupDefault?: string | null;
  /**
   * When true, the dialog does not load or save state to localStorage. Used
   * by the "Launch again" flow so seeding the dialog never clobbers the
   * draft the user may have in the primary Launch dialog.
   */
  disablePersistence?: boolean;
  /** Override the dialog title (defaults to "Launch Agent"). */
  title?: string;
  /** Override the dialog description (defaults to the persona-aware copy). */
  description?: string;
  /** When set, open the dialog pre-filled from this server-side draft. */
  draftId?: string | null;
}

// Reactively track whether a guided tour is running, signalled by the tutorial
// overlay stamping `data-tutorial-active` on <html>. A plain synchronous read is
// only recomputed when the dialog happens to re-render, so when a tour *ends*
// (the attribute is removed) nothing re-renders the dialog — leaving it stuck in
// "tour mode" where Escape and outside-clicks are suppressed and, since it is
// modal, the whole page stays locked. Subscribing to the attribute via
// `useSyncExternalStore` makes the dialog re-render the instant the tour clears,
// restoring normal dismissal. Kept boundary-free (no tutorial-plugin import) on
// purpose. `getSnapshot` reads synchronously so the first render still sees the
// right value when a tour is what opened the dialog.
function subscribeTutorialActive(onChange: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-tutorial-active'],
  });
  return () => observer.disconnect();
}

function useTutorialActive(): boolean {
  return useSyncExternalStore(
    subscribeTutorialActive,
    () =>
      typeof document !== 'undefined' && document.documentElement.dataset.tutorialActive != null,
    () => false,
  );
}

/**
 * Whether a dismiss interaction started in a surface portaled to <body>
 * (outside DialogContent). Radix treats those clicks as "outside" and would
 * dismiss the dialog; keep it open instead. Covers the @-mention menu and
 * popovers such as the model picker, which overflow the modal on purpose.
 */
function isWithinPortaledOverlay(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null;
  return Boolean(
    el?.closest('[data-testid="launch-mention-menu"]') ||
    el?.closest('[data-slot="popover-content"]'),
  );
}

export function LaunchAgentDialog({
  open,
  onOpenChange,
  onSuccess,
  defaults,
  attachmentDefaults,
  groupDefault,
  disablePersistence,
  title,
  description,
  draftId,
}: LaunchAgentDialogProps) {
  const agentTypeRegistry = useAgentTypeRegistry();
  const startAgent = useStartAgent();
  const resolvePr = useResolveGitHubPr();
  const { data: blueprints = [] } = useBlueprints();
  // Snapshots join the blueprint picker only for orgs entitled to the feature.
  // `devbox-snapshots` is an org capability (feature flag), not an RBAC slug —
  // auth-disabled self-host passes; otherwise strictly the active org's set.
  const canReadSnapshots = useHasCapability(DEVBOX_SNAPSHOTS_CAPABILITY);
  const { data: snapshots = [] } = useSnapshots({ enabled: open && canReadSnapshots });
  const baseBuilding = useBaseBlueprintBuilding();
  const { data: personas = [] } = useAgentPersonas();
  const { data: agentGroups = [] } = useAgentGroups();
  // The "computer use" capability toggle is offered only when the org has the
  // computer-use plugin installed (its manifest is served for installed orgs).
  const { manifests: installedPluginManifests } = usePlugins();
  const computerUseAvailable = useMemo(
    () => installedPluginManifests.some((m) => m.name === 'computer-use'),
    [installedPluginManifests],
  );
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  const modelSupport = useAgentModelSupportHelpers();
  const { activeOrganizationId, activeOrganization } = useOrganization();
  const sharingPolicy = useSessionSharingPolicy();
  const { user: currentUser } = useAuth();
  const myTeams = useMyTeams();
  const { data: orgUsers = [] } = useUsers();
  // Members the session can be shared with directly — everyone but the caller.
  const shareableUsers = useMemo(
    () => orgUsers.filter((u) => u.id !== currentUser?.id),
    [orgUsers, currentUser?.id],
  );
  // Shares picked ahead of launch. The grants are only posted once the launch
  // succeeds (the agent must exist first); a failed grant never fails the
  // launch. Resource grants are org-scoped, so the affordance is hidden when
  // no org is active — the sentinel org id below only partitions the cache
  // and the mutation cannot fire without an active org. It deliberately is
  // NOT `useOrgKey`'s `'global'` sentinel: if one ever does fire, the id
  // should read as a bug in the logs rather than a plausible org.
  const [pendingShares, setPendingShares] = useState<PendingShare[]>([]);
  // Whether the picker has been touched. Until it has, the general-access row
  // is *derived* from the org default rather than stored, which is why there
  // is no seeding effect here: the row has to tell the truth about what
  // launching will do, and deriving it keeps that true even if the org's
  // default arrives after the dialog opened.
  const [sharesTouched, setSharesTouched] = useState(false);
  // What this org starts new sessions as. The launch sends the row back as
  // `orgShare` so the server (which applies the default for Slack, the CLI,
  // and the API too) does not also open one. Changing the row here overrides
  // the default for this launch only, "No access" included.
  const orgDefaultShare = effectiveDefaultSessionShare(activeOrganization);
  const defaultOrgShareRow = useMemo<PendingShare | null>(() => {
    const access = defaultSessionShareAccess(orgDefaultShare);
    if (!access || !activeOrganizationId) return null;
    return {
      subjectType: 'org',
      subjectId: activeOrganizationId,
      subjectLabel: `Everyone in ${activeOrganization?.name ?? 'the organization'}`,
      access,
    };
  }, [orgDefaultShare, activeOrganizationId, activeOrganization?.name]);
  // The shares the picker shows and the launch acts on. Once the user edits
  // the picker, `pendingShares` is the whole truth — the edit was made
  // against a list that already contained the derived row, so an explicit
  // "No access" survives as its absence.
  const shareSelections = useMemo(
    () =>
      sharesTouched || !defaultOrgShareRow ? pendingShares : [...pendingShares, defaultOrgShareRow],
    [sharesTouched, defaultOrgShareRow, pendingShares],
  );
  const handleSharesChange = useCallback((next: PendingShare[]) => {
    setSharesTouched(true);
    setPendingShares(next);
  }, []);
  const createResourceGrant = useCreateResourceGrantMutation(
    asOrgId(activeOrganizationId ?? '_BROKEN_REQ_WITH_NO_ACTIVE_ORG'),
  );
  const sandboxHealth = useSandboxHealth(activeOrganizationId, open);
  const isTrial = sandboxHealth.data?.status === 'trial';
  // Org sandbox-size settings feed the size chip: the "(Default)" option
  // reflects the org's configured default and custom sizes become pickable.
  const orgCustomSizes = activeOrganization?.customSandboxSizes ?? [];
  const orgDefaultSandboxSize = activeOrganization?.defaultSandboxSize ?? null;

  // While a guided tour is running, ignore remembered selections and start from
  // fresh app defaults so every run of the tutorial lands in the same state
  // (which agent is selected, drawers collapsed, no leftover blueprint/env
  // vars). The tutorial overlay stamps `data-tutorial-active` on <html> for the
  // life of a tour; reading it keeps this boundary-free (no dependency on the
  // tutorial plugin). Tracked reactively (see `useTutorialActive`) so the dialog
  // re-renders the moment a tour ends and restores its normal dismissal — a
  // synchronous-only read would leave a tour-opened dialog stuck un-dismissable
  // (and the page locked) after the user exits the tour. The initial value is
  // still read synchronously, so the persisted-state load below sees the right
  // value on the first render after the dialog opens (a tour is what opens it).
  // Persistence is disabled both ways: we neither restore their config nor
  // overwrite it with the tour's throwaway state.
  const tutorialActive = useTutorialActive();
  const persistenceDisabled = Boolean(disablePersistence) || tutorialActive;

  // Fall back to the first registered agent type when the server hasn't returned a default
  // (e.g., all agents disabled, or catalog still loading). The actual launch attempt will be
  // rejected server-side with 403 if the selected type is disabled.
  const firstRegisteredType = agentTypeRegistry.getAll()[0]?.type;
  const effectiveDefaultAgentType = (defaults?.agentType ??
    modelSupport.defaultAgentType ??
    firstRegisteredType) as AgentType;

  const [state, dispatch] = useReducer(
    launchFormReducer,
    // Spread rather than list: `createInitialState` normalizes every seed
    // value, so a field is dropped here only when a caller means to override
    // it. Hand-listing is what let "Launch again" silently lose the sandbox
    // config the source run was launched with.
    {
      ...defaults,
      agentType: effectiveDefaultAgentType,
      model: defaults?.model ?? agentTypeRegistry.getDefaultModel(effectiveDefaultAgentType) ?? '',
      attachmentValues: mergeAttachmentDefaults(defaults?.attachmentValues, attachmentDefaults),
      // A route handoff picks the group; the seeded run's own filing is the
      // fallback.
      groupId: groupDefault ?? defaults?.groupId,
    },
    createInitialState,
  );
  const {
    agentType,
    selectedProviderId,
    model,
    modelEndpointId,
    manualBlueprintName,
    snapshotId,
    resourceSize,
    customSize,
    dockerd,
    computerUse,
    idleTimeMinutes,
    attachmentValues,
    envVars,
    chatInputKey,
    selectedPersonaId,
    initialPrompt,
    chatAttachments,
    selectedProviderSecretId,
    reasoningEffort,
    promptMode,
    labelIds,
    groupId,
  } = state;
  const planModeEnabled = useFeatureFlag('plan_mode_enabled');
  const submitRef = useRef<(() => void) | null>(null);
  // Escape ladders through the open @-mention menu before it can close the
  // dialog. Radix dismisses on Escape from a document/capture listener that
  // runs before the composer's own handler, so the composer keeps this ref
  // pointed at a handler we call from `onEscapeKeyDown`.
  const mentionEscapeRef = useRef<(() => boolean) | null>(null);
  // Points at the composer's imperative "open the `@`-menu" handler so the
  // "Add context" button in the applied-context row can trigger the same menu.
  const openMentionRef = useRef<(() => void) | null>(null);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  // Set after a successful launch. We don't reset the form on close (that would
  // mutate the still-visible dialog mid-exit-animation — the ChatInput remounts
  // on its `key` change — which flashes). Instead we defer the reset to the next
  // open, applied before paint so the form is simply fresh when shown.
  const pendingResetRef = useRef(false);
  // True from the moment Launch is clicked until the dialog next opens. We keep
  // the form rendered in its disabled "launching" state through the entire close
  // animation: the mutation's `isPending` flips back to false the instant the
  // POST resolves, but the dialog is still mounted and fading out, so without
  // this the content would re-enable/reflow mid-fade (a visible flash). Cleared
  // on the next open so a freshly reopened dialog is interactive again.
  const [launching, setLaunching] = useState(false);
  // Clear the freeze when the dialog (re)opens. Done during render via React's
  // "reset state when a prop changes" pattern (track the previous `open`, adjust
  // when it flips) rather than in an effect: a synchronous setState in an effect
  // trips the cascading-render lint and would also paint one frozen frame before
  // clearing. Setting `false` while already false is a no-op React bails on, so
  // a normal open doesn't cause an extra render.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setLaunching(false);
  }
  const [baseline, setBaseline] = useState<LaunchFormState | null>(null);
  const [formReady, setFormReady] = useState(false);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(draftId ?? null);
  const { data: allDrafts = [] } = useAgentDrafts();
  const loadedDraft = useMemo(
    () => (draftId ? allDrafts.find((d) => d.id === draftId) : undefined),
    [allDrafts, draftId],
  );
  // Tracks which `draftId` has already been hydrated into the form for the
  // current open. Autosave (and WS draft updates) replace the cached draft
  // object on every keystroke, giving `loadedDraft` a new reference; without
  // this guard the load effect re-runs mid-typing, re-dispatches
  // `load_persisted`, remounts the ChatInput, and snaps the caret to the
  // start. Reset to `null` when the dialog closes so the next open re-hydrates.
  const hydratedDraftIdRef = useRef<string | null>(null);
  // Flipped on once the user has clicked Launch with a missing required key,
  // so the chip switches from the highlighted-picker hint to an inline
  // "Choose a provider key" error. Reset when a key is selected or the dialog
  // closes — pre-submit state should never show the error.
  const [missingKeySubmitAttempted, setMissingKeySubmitAttempted] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // The inline "Add new …" create surface requested from the `@`-mention menu,
  // or `null`. Hosted here (not inside `LaunchMentionMenu`) because the menu
  // unmounts the instant the composer blurs — which is exactly what opening the
  // create modal does. The plugin create component owns its own modal.
  const [mentionCreate, setMentionCreate] = useState<LaunchMentionCreateRequest | null>(null);
  // The inline "Edit …" surface requested from a mention row's cog, or `null`.
  // Hosted here for the same reason as `mentionCreate`.
  const [mentionEdit, setMentionEdit] = useState<LaunchMentionEditRequest | null>(null);
  // Bumped on Clear to remount the config section so its internal collapse
  // state (and any uncontrolled picker state) resets along with the values.
  const [configSectionKey, setConfigSectionKey] = useState(0);

  // Mounting the full agent-config section (attachments editor + advanced
  // picker chips) is the bulk of the open-time work — doing it in the same
  // commit as the dialog shell produces a single ~70-170ms blocking task that
  // makes the dialog feel slow to open. Defer it to the frame after the shell
  // and composer have painted: the dialog appears instantly and the config
  // section fills in ~1 frame later (imperceptible, and it sits below the
  // composer). Resets to unmounted whenever the dialog closes.
  const [configMounted, setConfigMounted] = useState(false);
  // Tracks the last PR link we resolved into a git-repo attachment, so detecting
  // a pasted PR fires the lookup once rather than on every keystroke.
  const handledPrUrlRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) return;
    // Double rAF so the config mount lands in the frame *after* the shell has
    // painted. A single rAF fires before the next paint and coalesces into the
    // same animation frame as the shell commit, defeating the deferral.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setConfigMounted(true));
    });
    // Cleanup runs when the dialog closes (open → false), resetting the gate so
    // the deferral applies again on the next open instead of mounting eagerly.
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      setConfigMounted(false);
    };
  }, [open]);

  const isMatrixDriven = modelSupport.isMatrixDriven(agentType);

  const availableProviders = useMemo(
    () => modelSupport.getProvidersForAgent(agentType),
    [modelSupport, agentType],
  );
  const showProviderSelect = isMatrixDriven && availableProviders.length > 1;

  const effectiveProviderId = useMemo(() => {
    if (!isMatrixDriven || availableProviders.length === 0) return null;
    if (selectedProviderId && availableProviders.some((p) => p.id === selectedProviderId)) {
      return selectedProviderId;
    }
    if (model) {
      const modelProviderId = modelSupport.getProviderForModel(agentType, model);
      if (availableProviders.some((p) => p.id === modelProviderId)) {
        return modelProviderId;
      }
    }
    const curatedDefault = modelSupport.getDefaultProvider(agentType);
    if (curatedDefault && availableProviders.some((p) => p.id === curatedDefault)) {
      return curatedDefault;
    }
    return availableProviders[0]?.id ?? null;
  }, [isMatrixDriven, availableProviders, selectedProviderId, model, modelSupport, agentType]);

  const modelOptions = useMemo(() => {
    if (isMatrixDriven) {
      const allModels = modelSupport.getDiscoveredModels(agentType);
      const filtered =
        showProviderSelect && effectiveProviderId
          ? allModels.filter((m) => m.providerId === effectiveProviderId)
          : allModels;
      return filtered.map((m) => ({
        id: m.id,
        name: m.displayName,
        providerId: m.providerId,
      }));
    }
    return agentTypeRegistry.get(agentType)?.models ?? [];
  }, [
    isMatrixDriven,
    showProviderSelect,
    effectiveProviderId,
    modelSupport,
    agentType,
    agentTypeRegistry,
  ]);

  const launchDefaults = useLaunchAgentDefaults({
    modelSupport,
    agentTypeRegistry,
  });

  const {
    getDefaultProviderForAgentType,
    getDefaultModelForAgentType,
    getDefaultEndpointForModel,
    getDefaultEndpointForAgentType,
  } = launchDefaults;

  const { markUserPickedAgentType, markRestoredAgentType, resetAgentTypePreference } =
    useLaunchAgentSync({
      open,
      isMatrixDriven,
      modelSupport,
      state: { agentType, model, selectedProviderId },
      modelOptions,
      effectiveProviderId,
      defaults: launchDefaults,
      dispatch,
      selectedPersonaId,
      seededAgentType: defaults?.agentType != null,
    });

  const providerId = useMemo(
    () => modelSupport.getProviderForModel(agentType, model),
    [modelSupport, agentType, model],
  );
  // The matrix provider entry for the selected provider drives both the
  // canonical key-picker provider and which secret types are accepted.
  const providerEntry = useMemo(
    () => modelSupport.getProviderEntry(agentType, effectiveProviderId),
    [modelSupport, agentType, effectiveProviderId],
  );
  // Secret types this provider accepts. Defaults to `['apiKey']` for
  // providers (or non-matrix agents) that don't declare it; an empty array
  // means the provider is free-tier and needs no key.
  const keyTypes = useMemo<readonly ModelProviderSecretType[]>(
    () => providerEntry?.keyTypes ?? ['apiKey'],
    [providerEntry],
  );
  const requiresKey = keyTypes.length > 0;
  // Canonical provider for the key picker (strict filter) + the chosen
  // secret. Derived from the selected provider first (so the picker follows
  // the agent/provider dropdown even before a model is chosen), then the
  // model heuristic. Both fetch from the same React Query cache as the
  // picker, so this read is deduped, not an extra round trip.
  const modelProvider = useMemo(
    () => toModelProvider(providerEntry?.modelProvider ?? effectiveProviderId ?? providerId, model),
    [providerEntry, effectiveProviderId, providerId, model],
  );
  // Drop a stale key selection when switching to a free-tier provider so it
  // isn't sent on launch and the picker re-applies the remembered pick on the
  // way back to a keyed provider.
  useEffect(() => {
    if (!requiresKey && selectedProviderSecretId) {
      dispatch({ type: 'set_field', field: 'selectedProviderSecretId', value: null });
    }
  }, [requiresKey, selectedProviderSecretId]);
  // Derive — the post-submit "missing key" error only applies while a key
  // is still required and unpicked. Picking a key (or switching to a
  // free-tier provider) silently clears it on the next render without
  // needing a reset effect.
  const showMissingKeyError = missingKeySubmitAttempted && requiresKey && !selectedProviderSecretId;
  const { secrets: accessibleSecrets } = useAccessibleModelProviderSecrets({
    types: ['apiKey', 'subscription'],
  });
  const selectedSecret = useMemo(
    () => accessibleSecrets.find((s) => s.id === selectedProviderSecretId) ?? null,
    [accessibleSecrets, selectedProviderSecretId],
  );
  const allEndpoints = useMemo(
    () => (isMatrixDriven ? modelSupport.getEndpointsForProvider(agentType, providerId) : []),
    [isMatrixDriven, modelSupport, agentType, providerId],
  );
  const availableEndpoints = useMemo(
    () => allEndpoints.filter((e) => modelSupport.isEndpointAvailable(agentType, e.id)),
    [allEndpoints, modelSupport, agentType],
  );
  // A picked provider key (for the current provider) changes endpoint
  // launchability beyond the server's host-credential view:
  //   - a subscription key bypasses org-key endpoint credentials entirely
  //     (legacy `codex-subscription` / `claude-max` behaviour), and
  //   - a supplied API key provides the endpoint's secret-backed credential,
  //     so an endpoint blocked only on `fromSecret` requirements is launchable.
  const hasMatchingKey = selectedSecret != null && selectedSecret.provider === modelProvider;
  const subscriptionBypass =
    selectedSecret?.type === 'subscription' && keyTypes.includes('subscription');
  const launchableEndpoints = useMemo(() => {
    if (!hasMatchingKey) return availableEndpoints;
    if (subscriptionBypass) return allEndpoints;
    return allEndpoints.filter(isEndpointSatisfiableByProviderKey);
  }, [hasMatchingKey, subscriptionBypass, availableEndpoints, allEndpoints]);

  const { effectiveModelEndpointRouteId, hasNoAvailableEndpoint } = useEffectiveModelEndpoint({
    isMatrixDriven,
    modelEndpointId,
    agentType,
    model,
    allEndpoints,
    availableEndpoints: launchableEndpoints,
    modelSupport,
  });

  const gitRepo = useMemo(() => readGitRepoAttachment(attachmentValues), [attachmentValues]);
  const repoSlug = gitRepo?.repoSlug ?? '';
  const chatAttachmentsOverLimit = useMemo(
    () =>
      chatAttachments.reduce((total, attachment) => total + (attachment.size ?? 0), 0) >
      MAX_ATTACHMENTS_TOTAL_BYTES,
    [chatAttachments],
  );

  const availableBlueprints = useAvailableBlueprints(blueprints, repoSlug);

  const selectedBlueprintName = useMemo(
    () => resolveLaunchBlueprintName(manualBlueprintName, availableBlueprints, repoSlug),
    [manualBlueprintName, availableBlueprints, repoSlug],
  );

  const isDirty = useMemo(
    () => (baseline ? isLaunchFormDirty(state, baseline) : false),
    [state, baseline],
  );

  const getDraftPayload = useCallback(
    () => launchFormToDraftPayload(state, selectedBlueprintName),
    [state, selectedBlueprintName],
  );

  const { flush, discard, saveStatus } = useAgentDraftAutosave({
    enabled: open && !persistenceDisabled,
    ready: formReady,
    isDirty,
    revision: state,
    getPayload: getDraftPayload,
    draftId: activeDraftId,
    onDraftId: setActiveDraftId,
  });

  const flushDraftRef = useRef(flush);
  const discardDraftRef = useRef(discard);
  useEffect(() => {
    flushDraftRef.current = flush;
    discardDraftRef.current = discard;
  }, [flush, discard]);

  function resetForm() {
    dispatch({
      type: 'reset',
      agentType: effectiveDefaultAgentType,
      defaultModel: getDefaultModelForAgentType(effectiveDefaultAgentType),
      defaultEndpointRouteId: getDefaultEndpointForAgentType(effectiveDefaultAgentType),
    });
  }

  async function clearForm() {
    if (!persistenceDisabled) {
      // Block the open effect from re-hydrating while discard updates the cache.
      if (draftId) {
        hydratedDraftIdRef.current = draftId;
      }
      clearPersistedFormState(activeOrganizationId);
      await discardDraftRef.current();
      setActiveDraftId(null);
    }
    resetForm();
    setPendingShares([]);
    setSharesTouched(false);
    setDetailsOpen(false);
    setConfigSectionKey((k) => k + 1);
    queueMicrotask(() => {
      setBaseline(stateRef.current);
    });
    toast.success('Form cleared');
  }

  // Every dismissal — Cancel, click outside, Escape, the mobile X — persists
  // what is on screen right now. Autosave is debounced, so the last keystrokes
  // are typically still pending; the payload is captured here, synchronously,
  // before the teardown below drops `formReady`/`baseline`/`activeDraftId`,
  // because a save that re-read those values would write a stale draft (or a
  // duplicate one, having lost the id).
  function handleOpenChange(next: boolean) {
    if (!next) {
      flushDraftRef
        .current({ payload: getDraftPayload(), isDirty })
        .catch(() => toast.error('Failed to save draft'));
      setDetailsOpen(false);
      setPendingShares([]);
      setSharesTouched(false);
      setFormReady(false);
      setBaseline(null);
      setActiveDraftId(null);
      hydratedDraftIdRef.current = null;
    }
    onOpenChange(next);
  }

  // Detect a pasted (or typed) GitHub PR link and pre-fill the git-repo
  // attachment with the PR's repo and head branch, so launching against a PR is
  // one paste. Resolves each unique PR link once; stays quiet on failure (e.g.
  // the user hasn't linked GitHub) so it never nags mid-typing.
  function handlePromptChange(value: string) {
    dispatch({ type: 'set_field', field: 'initialPrompt', value });
    const pr = promptPullRequest(value);
    if (!pr) return;
    const key = `${pr.owner}/${pr.repo}#${pr.number}`;
    if (handledPrUrlRef.current === key) return;
    handledPrUrlRef.current = key;
    void resolvePr
      .mutateAsync({ owner: pr.owner, repo: pr.repo, number: pr.number })
      .then((res) => {
        dispatch({
          type: 'set_attachment',
          id: GIT_REPO_ATTACHMENT_ID,
          value: {
            repoSlug: res.repoSlug,
            repoBranch: res.repoBranch,
          } satisfies GitRepoAttachmentConfig,
          pluginName: 'github',
        });
        toast.success('Repository set from pull request', {
          description: `${res.repoSlug} @ ${res.repoBranch}`,
        });
      })
      .catch(() => {
        // Leave `handledPrUrlRef` set so a failed lookup isn't retried on every
        // keystroke while the link stays in the prompt.
      });
  }

  function handleCreate(prompt: string, attachments: Attachment[]) {
    // Update chat attachments in state for persistence
    dispatch({ type: 'set_chat_attachments', attachments });
    // `prompt` is the composer's serialized text: inline agent-run chips already
    // appear as their `@[title](agent-run:id)` markers, which the server unrolls
    // into full context before the prompt reaches the agent.
    if (!prompt.trim()) return;
    if (requiresKey && !selectedProviderSecretId) {
      // Surface the missing-key state inline (next to the picker) instead of
      // a toast — the picker is now ring-highlighted so the fix is obvious.
      setMissingKeySubmitAttempted(true);
      return;
    }
    if (hasNoAvailableEndpoint) {
      toast.error('No available endpoint route', {
        description:
          'The selected model has no compatible endpoint route with available credentials.',
      });
      return;
    }

    const selectedAttachments = sanitizeAttachments(attachmentValues);
    const sanitizedEnvVars = sanitizeEnvVars(envVars);

    // Inject reasoning effort as an env var when the agent type declares reasoning options
    const agentTypeInfo = agentTypeRegistry.get(agentType);
    if (agentTypeInfo?.reasoningEnvVar && agentTypeInfo.reasoningOptions?.length) {
      const effort =
        reasoningEffort ??
        agentTypeInfo.reasoningOptions.find((o) => o.default)?.id ??
        agentTypeInfo.reasoningOptions[0]?.id;
      if (effort) {
        sanitizedEnvVars.push({ key: agentTypeInfo.reasoningEnvVar, value: effort });
      }
    }

    const inlineAttachments = attachments.filter(
      (a) => (a.type === 'image' || a.type === 'file') && a.data,
    );
    const content =
      inlineAttachments.length > 0 ? buildContentBlocks(prompt, attachments) : undefined;

    // Collapse the launch-time picks into a single sandbox-provider options
    // object so server-side handling has one canonical place to read from.
    // Legacy `blueprintName` is still set for clients/services that haven't
    // migrated to the structured field.
    // When the user hasn't picked a size, inherit the selected blueprint's
    // baked-in size so the launch matches what the size chip shows ("from
    // <blueprint>"). A snapshot launch has no blueprint to inherit from.
    const selectedBlueprintForSize = snapshotId
      ? null
      : (availableBlueprints.find((bp) => bp.name === selectedBlueprintName) ?? null);
    const effectiveSize = resolveEffectiveSandboxSize({
      resourceSize,
      customSize,
      blueprint: selectedBlueprintForSize,
      isTrial,
    });
    const trialSizeMustClamp =
      isTrial &&
      (effectiveSize.customSize != null ||
        (effectiveSize.resourceSize && !isResourceSizeAllowedForTrial(effectiveSize.resourceSize)));
    const sandboxOverrides = {
      ...(trialSizeMustClamp
        ? { resourceSize: MAX_TRIAL_RESOURCE_SIZE }
        : effectiveSize.resourceSize
          ? { resourceSize: effectiveSize.resourceSize }
          : {}),
      ...(!isTrial && effectiveSize.customSize ? { customSize: effectiveSize.customSize } : {}),
      ...(dockerd ? { dockerd: true } : {}),
      ...(computerUse ? { computerUse: true } : {}),
      ...(idleTimeMinutes ? { idleTimeMinutes } : {}),
      ...(snapshotId ? { snapshotId } : {}),
    };
    // A picked snapshot replaces the blueprint outright — the server rejects
    // launches that carry both, so the resolved base-blueprint name must not
    // ride along.
    const sandboxOptions = resolveSandboxOptions({
      blueprintName: snapshotId ? null : selectedBlueprintName,
      ...(Object.keys(sandboxOverrides).length ? { sandboxOptions: sandboxOverrides } : {}),
    });

    // Freeze the form before the mutation runs and keep it frozen through the
    // close animation (see `launching` above). `startAgent.isPending` alone is
    // not enough: it flips back to false the instant the POST resolves, while
    // the dialog is still fading out.
    setLaunching(true);
    startAgent.mutate(
      {
        agentType,
        prompt,
        ...(selectedPersonaId ? { personaId: selectedPersonaId } : {}),
        ...(model ? { model } : {}),
        ...(effectiveModelEndpointRouteId
          ? { modelEndpointId: effectiveModelEndpointRouteId }
          : {}),
        ...(state.systemPrompt ? { systemPrompt: state.systemPrompt } : {}),
        ...(selectedBlueprintName && !snapshotId ? { blueprintName: selectedBlueprintName } : {}),
        ...(sandboxOptions ? { sandboxOptions } : {}),
        ...(content ? { content } : {}),
        ...(selectedAttachments.length ? { attachments: selectedAttachments } : {}),
        ...(sanitizedEnvVars.length ? { envVars: sanitizedEnvVars } : {}),
        ...(labelIds.length ? { labelIds } : {}),
        ...(groupId ? { groupId } : {}),
        ...(requiresKey && selectedProviderSecretId
          ? { providerSecretId: selectedProviderSecretId }
          : {}),
        ...(promptMode ? { promptMode } : {}),
        // The org-wide row is the server's to open, at creation, so the
        // session is never briefly private to its launcher while a
        // follow-up grant is in flight.
        //
        // Sent ONLY when the picker was touched, and that is load-bearing
        // rather than an optimisation: an untouched picker is showing the
        // org default it derived for display, and echoing that value back
        // would be indistinguishable from a member choosing it. The server
        // tags the grant it opens off the presence of this field, so
        // echoing would file every ordinary launch as a deliberate share
        // and bury the security Sharing page under one row per session.
        // Untouched, the server resolves the default itself — which it must
        // do anyway for Slack, the CLI, and the API.
        //
        // Touched, the answer is sent including `'none'`, because the
        // picker has to be able to opt this session out of an org default.
        ...(activeOrganizationId && sharesTouched
          ? {
              orgShare:
                shareSelections.find((share) => share.subjectType === 'org')?.access ?? 'none',
            }
          : {}),
      },
      {
        onSuccess: (agent) => {
          // The agent now exists, so the pending shares can be granted. Fired
          // and forgotten via `mutateAsync` (the promises settle even after
          // this dialog unmounts behind the navigation); a failed grant only
          // toasts — the launch itself already succeeded.
          //
          // The org row is included, even though the server opens it at
          // creation, because `ensureResourceGrant` is idempotent: an active
          // grant with this identity already on the session returns it
          // untouched, source and all. So this costs one no-op call in the
          // normal case and is the safety net in the one case that matters —
          // a rolling deploy, where an older pod strips the `orgShare` key it
          // does not know and has no notion of a default, creating the
          // session private with no error for anyone to see. Self-healing
          // beats a silent access regression for the length of a rollout.
          const shares = shareSelections;
          // Clear the picker unconditionally, before the grants fire. The
          // success path closes the dialog directly rather than through
          // `handleOpenChange` (see the ordering note below), so this is the
          // only place the share state is torn down — and leaving it set
          // would carry the last launch's selection, `sharesTouched`
          // included, into the next launch from this same mounted dialog:
          // the org default would be silently overridden by a stale answer.
          // Outside the `shares.length` branch on purpose, because "org set
          // to No access, nothing else shared" is an empty list that still
          // has to be forgotten.
          setPendingShares([]);
          setSharesTouched(false);
          if (shares.length > 0 && activeOrganizationId) {
            for (const share of shares) {
              createResourceGrant
                .mutateAsync({
                  data: {
                    resourceType: 'agent',
                    resourceId: agent.id,
                    subjectType: share.subjectType,
                    subjectId: share.subjectId,
                    roleId: resourceGrantRoleId('agent', share.access),
                  },
                })
                .catch((err: unknown) => {
                  toast.error('Session launched, but sharing failed', {
                    description:
                      `Could not share with ${share.subjectLabel}. ${err instanceof Error ? err.message : ''}`.trim(),
                  });
                });
            }
          }
          // Discard the server draft (the prompt) but keep the durable
          // localStorage config: the next open should come up with the
          // just-launched agent type/model/env vars and an empty composer.
          // The deferred reset (applied on the next open) clears the in-memory
          // form (prompt, chat attachments, snapshot) before paint; the
          // open-time load effect then re-hydrates the saved config on top of
          // it. Persisted config never includes the prompt, and the draft is
          // discarded here, so nothing re-fills the composer. Only the explicit
          // Clear action wipes the saved config.
          void discardDraftRef.current();
          pendingResetRef.current = true;
          // Close the dialog and hand the agent to `onSuccess` (which navigates
          // into it) in the same tick. Both are plain state updates with no
          // dependency on the close animation or a timer, so a skipped or
          // interrupted exit animation — or a call site whose `open` derives
          // from async URL state — can never strand the dialog half-open with
          // the agent already navigated in underneath. Order matters: close
          // first, because a call site's `onOpenChange` reads its render-time
          // location, so running it after a navigation would strip the query
          // off the *previous* path. The heavy agent view mounts as a
          // transition at the call site, so it stays low-priority behind the
          // modal's exit fade; `launching` keeps the form frozen through that
          // fade and is cleared on the next open.
          onOpenChange(false);
          onSuccess(agent);
        },
        onError: (err: Error) => {
          // Unfreeze so the user can fix the input and retry; the dialog stays
          // open on error (no close to defer the unfreeze to).
          setLaunching(false);
          toast.error('Failed to launch agent', { description: err.message });
        },
      },
    );
  }

  function handlePersonaChange(persona: AgentPersona | null) {
    if (!persona) {
      dispatch({
        type: 'clear_persona',
        agentType: state.agentType,
        defaultModel: getDefaultModelForAgentType(state.agentType),
        defaultEndpointRouteId: getDefaultEndpointForAgentType(state.agentType),
      });
      setDetailsOpen(false);
      return;
    }
    dispatch({ type: 'apply_persona', persona });
  }

  function handleClearPersona() {
    dispatch({
      type: 'clear_persona',
      agentType: state.agentType,
      defaultModel: getDefaultModelForAgentType(state.agentType),
      defaultEndpointRouteId: getDefaultEndpointForAgentType(state.agentType),
    });
    setDetailsOpen(false);
  }

  // Apply a selection from the `@`-mention menu to the launch config. Personas
  // route through the existing persona reducer; everything else is a generic
  // plugin attachment set on `attachmentValues`. The text token is removed by
  // the composer (see `clearMentionToken` below), since a mention configures
  // the run rather than inserting an inline reference.
  function handleMentionApply(result: LaunchMentionResult): MentionApplyAction {
    // Resolve personas from the live query cache rather than the render-scope
    // list: the inline "Add persona" create surface applies the new persona
    // synchronously in its mutation's `onSuccess`, one tick before this
    // component re-renders with the appended persona, so `personas` here would
    // still be stale and the apply would silently no-op.
    const livePersonas =
      queryClient.getQueryData<AgentPersona[]>(agentPersonasKey(orgKey)) ?? personas;
    const action = resolveMentionApply(result, livePersonas);
    switch (action.type) {
      case 'apply-persona':
        handlePersonaChange(action.persona);
        break;
      case 'set-attachment':
        dispatch({
          type: 'set_attachment',
          id: action.id,
          value: action.config,
          pluginName: action.pluginName,
        });
        break;
      case 'set-sandbox':
        dispatch({
          type: 'set_sandbox',
          resourceSize: action.resourceSize,
          capabilityId: action.capabilityId,
        });
        break;
      case 'custom':
        action.run();
        break;
      case 'reference-agent-run':
        // The inline chip is inserted into the composer by `insertMention` in
        // the render prop below (the editor owns the atomic chip); nothing to do
        // in form state.
        break;
      case 'noop':
        break;
    }
    return action;
  }

  function handleAgentTypeChange(type: AgentType) {
    markUserPickedAgentType();
    dispatch({
      type: 'set_agent_type',
      agentType: type,
      defaultProviderId: getDefaultProviderForAgentType(type),
      defaultModel: getDefaultModelForAgentType(type),
      defaultEndpointRouteId: getDefaultEndpointForAgentType(type),
    });
  }

  function handleModelChange(modelId: string) {
    const newProviderId = modelSupport.getProviderForModel(agentType, modelId);
    const validDefault = getDefaultEndpointForModel(agentType, modelId);
    dispatch({
      type: 'set_model',
      model: modelId,
      providerId: newProviderId,
      defaultEndpointRouteId: validDefault,
    });
  }

  function handleEndpointChange(endpointId: string) {
    dispatch({ type: 'set_field', field: 'modelEndpointId', value: endpointId });
  }

  // Track the org id whose state we last hydrated from, so we can detect an
  // org switch and wipe any in-memory data that belongs to the previous org.
  // `undefined` means "no load has completed yet" — distinct from `null`,
  // which is a valid org id (signed-out / no active org).
  const resetFormRef = useRef(resetForm);
  useEffect(() => {
    resetFormRef.current = resetForm;
  });
  const lastLoadedOrgIdRef = useRef<string | null | undefined>(undefined);
  const formHydratingRef = useRef(false);

  // Apply the deferred post-launch reset the next time the dialog opens. A
  // layout effect runs it before paint, so the form is already fresh on the
  // first painted frame of the new open (no visible reset). Runs ahead of the
  // persisted-load effect below, which then re-hydrates the saved config
  // (agent type, model, env vars, ...) on top of the reset — the reset only
  // stands for what persistence doesn't cover: the prompt, chat attachments,
  // and snapshot selection. (The launching freeze is dropped separately, during
  // render — see `launching` above — to keep this effect free of a setState.)
  useLayoutEffect(() => {
    if (open && pendingResetRef.current) {
      pendingResetRef.current = false;
      resetFormRef.current();
    }
  }, [open]);

  // Load persisted config or an existing server draft when the dialog opens.
  useEffect(() => {
    if (!open || persistenceDisabled) {
      return;
    }
    const orgIdAtLoad = activeOrganizationId;
    const prevOrgId = lastLoadedOrgIdRef.current;
    if (prevOrgId !== undefined && prevOrgId !== orgIdAtLoad) {
      resetAgentTypePreference();
      // A draft id is only meaningful inside its organization. Force a fresh
      // hydration even if routing leaves the same id prop mounted across the
      // org switch.
      hydratedDraftIdRef.current = null;
    }
    // Hydrate a given draft into the form only once per open. `loadedDraft`
    // gets a fresh reference on every autosave/WS update; re-running the load
    // would remount the ChatInput and reset the caret while the user types.
    if (draftId && hydratedDraftIdRef.current === draftId) return;
    // Close the save gate synchronously. Deferring only the rendered
    // `formReady` state would let the following save effect write the prior
    // org's state under the new org during this passive-effect flush.
    formHydratingRef.current = true;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setFormReady(false);
      setBaseline(null);
    });

    const applyLoaded = (
      persistedData: Extract<LaunchFormAction, { type: 'load_persisted' }>['persistedData'],
    ) => {
      // Preserve a remembered type if it is still enabled. Mark before
      // dispatch so the admin-default sync can validate it when support is
      // ready, rather than clobbering valid config or preserving a type the
      // org has since disabled.
      markRestoredAgentType(persistedData.agentType);
      dispatch({
        type: 'load_persisted',
        persistedData: {
          ...persistedData,
          attachmentValues: mergeAttachmentDefaults(
            persistedData.attachmentValues,
            attachmentDefaults,
          ),
          // A group asked for by the caller outranks the restored one for the
          // same reason the attachment defaults do: it names the run's intent,
          // where the restored value is only where the last run went.
          groupId: groupDefault ?? persistedData.groupId ?? null,
        },
      });
      queueMicrotask(() => {
        if (cancelled) return;
        formHydratingRef.current = false;
        setBaseline(stateRef.current);
        setFormReady(true);
      });
    };

    if (draftId && loadedDraft) {
      hydratedDraftIdRef.current = draftId;
      lastLoadedOrgIdRef.current = orgIdAtLoad;
      queueMicrotask(() => {
        if (cancelled) return;
        setActiveDraftId(draftId);
      });
      applyLoaded(draftPayloadToPersistedData(loadedDraft.payload));
      return () => {
        cancelled = true;
      };
    }

    loadFormState(orgIdAtLoad)
      .then((persistedData) => {
        if (cancelled) return;
        lastLoadedOrgIdRef.current = orgIdAtLoad;
        setActiveDraftId(null);
        if (persistedData) {
          applyLoaded({
            agentType: persistedData.agentType as AgentType,
            selectedProviderId: persistedData.selectedProviderId,
            model: persistedData.model,
            modelEndpointId: persistedData.modelEndpointId,
            manualBlueprintName: persistedData.manualBlueprintName,
            resourceSize: persistedData.resourceSize,
            customSize: persistedData.customSize,
            dockerd: persistedData.dockerd,
            computerUse: persistedData.computerUse,
            idleTimeMinutes: persistedData.idleTimeMinutes,
            attachmentValues: persistedData.attachmentValues,
            envVars: persistedData.envVars,
            initialPrompt: '',
            selectedPersonaId: persistedData.selectedPersonaId,
            systemPrompt: persistedData.systemPrompt,
            chatAttachments: [],
            selectedProviderSecretId: persistedData.selectedProviderSecretId,
            reasoningEffort: persistedData.reasoningEffort,
            promptMode: persistedData.promptMode,
            labelIds: persistedData.labelIds,
            groupId: persistedData.groupId,
          });
        } else {
          if (prevOrgId !== undefined && prevOrgId !== orgIdAtLoad) {
            resetFormRef.current();
          }
          queueMicrotask(() => {
            if (cancelled) return;
            formHydratingRef.current = false;
            setBaseline(stateRef.current);
            setFormReady(true);
          });
        }
      })
      .catch((error) => {
        console.warn('Failed to load persisted form state:', error);
        if (!cancelled) {
          queueMicrotask(() => {
            formHydratingRef.current = false;
            setBaseline(stateRef.current);
            setFormReady(true);
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    activeOrganizationId,
    persistenceDisabled,
    draftId,
    loadedDraft,
    attachmentDefaults,
    groupDefault,
    markRestoredAgentType,
    resetAgentTypePreference,
  ]);

  // Save durable config (localStorage) immediately when it changes.
  useEffect(() => {
    if (open && !persistenceDisabled && formReady && !formHydratingRef.current) {
      saveFormState(state, activeOrganizationId);
    }
  }, [open, state, activeOrganizationId, persistenceDisabled, formReady]);

  // While a guided tour is driving this dialog, it can ask us to pre-select a
  // specific agent + model (and pre-fill the prompt) by stamping
  // `data-tutorial-launch-agent` / `-launch-model` / `-launch-prompt` on <html>
  // (see the tutorial overlay's `htmlSignals`). The launch tour uses the
  // agent/model pair to switch to the key-less free model right after it has
  // explained the provider key, so the final Launch step is reachable without
  // any provider setup; the sandbox-monitor tour adds the prompt so the user
  // can launch a busy box in one click. Kept boundary-free: we read DOM signals rather
  // than importing the tutorial plugin, and only honor them while a tour is
  // active. A "latest" ref keeps the handlers fresh without re-subscribing the
  // observer on every render.
  const applyTutorialSelectionRef = useRef<
    (agent: string | null, model: string | null, prompt: string | null) => void
  >(() => {});
  // The prompt is guarded separately from the agent/model signature: the
  // catalog-load re-apply below resets that signature (so a late agent/model
  // switch lands), but the prompt doesn't depend on the catalog, and
  // re-dispatching it would remount the composer. Only fill once per distinct
  // prompt; the close effect resets this so a later tour re-fills.
  const appliedTutorialPromptRef = useRef<string | null>(null);
  useEffect(() => {
    applyTutorialSelectionRef.current = (agent, model, prompt) => {
      if (agent) handleAgentTypeChange(agent as AgentType);
      if (model) handleModelChange(model);
      if (prompt && prompt !== appliedTutorialPromptRef.current) {
        appliedTutorialPromptRef.current = prompt;
        dispatch({ type: 'set_prompt', prompt });
      }
    };
  });
  useEffect(() => {
    if (!open) appliedTutorialPromptRef.current = null;
  }, [open]);
  const appliedTutorialSignalRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !tutorialActive || typeof document === 'undefined') return;
    const root = document.documentElement;
    // Re-apply when the selected agent's model list changes. A tour can hit the
    // free-model step before that agent's catalog has loaded (e.g. the sandbox
    // tour switches agents only a step or two after opening the dialog), in
    // which case the model switch resolves to an empty default and the model
    // sync then clears it. Keying on `modelOptions.length` means we forget what
    // we last applied and re-apply the signal once the options arrive, so the
    // selection lands regardless of how early the step fires. Re-applying is
    // idempotent (same agent/model) and safe during a tour, where the user
    // isn't hand-picking the agent.
    appliedTutorialSignalRef.current = null;
    const apply = () => {
      const wantAgent = root.getAttribute('data-tutorial-launch-agent');
      const wantModel = root.getAttribute('data-tutorial-launch-model');
      const wantPrompt = root.getAttribute('data-tutorial-launch-prompt');
      // Apply once per distinct signal so we don't fight the user if they tweak
      // the selection afterward, and so re-renders don't re-dispatch.
      const signature = `${wantAgent ?? ''}|${wantModel ?? ''}|${wantPrompt ?? ''}`;
      // Signal cleared (step left): forget it so re-entering the step re-applies.
      if (signature === '||') {
        appliedTutorialSignalRef.current = null;
        return;
      }
      if (signature === appliedTutorialSignalRef.current) return;
      appliedTutorialSignalRef.current = signature;
      applyTutorialSelectionRef.current(wantAgent, wantModel, wantPrompt);
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(root, {
      attributes: true,
      attributeFilter: [
        'data-tutorial-launch-agent',
        'data-tutorial-launch-model',
        'data-tutorial-launch-prompt',
      ],
    });
    return () => observer.disconnect();
  }, [open, tutorialActive, modelOptions.length]);

  const activePersona = selectedPersonaId ? personas.find((p) => p.id === selectedPersonaId) : null;
  // The persona detail card's collapse animation needs something to render
  // during its exit, after `activePersona` has already gone null. Remember
  // the last non-null persona (React's "adjust state during render" pattern,
  // same as `wasOpen` above) so the collapse can play instead of unmounting
  // its content in the same frame the persona is cleared.
  const [lastPersona, setLastPersona] = useState(activePersona);
  if (activePersona && activePersona !== lastPersona) {
    setLastPersona(activePersona);
  }
  const shownPersona = activePersona ?? lastPersona;
  // Resolve the picked group to its name for the applied-context chip. An id
  // the catalog no longer holds resolves to nothing and simply shows no chip —
  // the launch itself already tolerates a stale group id.
  const activeGroup = groupId ? (agentGroups.find((g) => g.id === groupId) ?? null) : null;

  const missingRequiredKey = requiresKey && !selectedProviderSecretId;
  // `startAgent.isPending` alone isn't enough: it flips back to false the
  // instant the POST resolves, while the dialog is still frozen/fading out
  // via `launching` (see its declaration above).
  const launchPending = launching || startAgent.isPending;
  const cannotLaunch =
    baseBuilding.isBuilding ||
    startAgent.isPending ||
    launching ||
    chatAttachmentsOverLimit ||
    // When a key is required but not picked, the launchable-endpoints filter
    // also drops everything. Keep Launch clickable so submitting surfaces the
    // inline "missing key" error next to the highlighted picker instead of
    // leaving the user with a disabled button and no actionable hint.
    (!missingRequiredKey && hasNoAvailableEndpoint);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        // Below `sm` the centered floating card is unusable — the software
        // keyboard clips a vertically-centered dialog and 90vh misjudges
        // mobile browser chrome — so the dialog becomes a full-height sheet
        // pinned to the *top* of the screen with `h-app` height (dynamic
        // viewport, or the visual-viewport height while the keyboard is open).
        // Top-anchoring keeps the header in place when the keyboard opens: the
        // sheet shrinks from the bottom up above the keyboard instead of a
        // bottom-anchored sheet sliding its top edge down out of reach.
        className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 max-sm:top-0 max-sm:h-app max-sm:max-h-app max-sm:max-w-full max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0 max-sm:data-[state=open]:zoom-in-100 max-sm:data-[state=open]:slide-in-from-bottom max-sm:data-[state=closed]:zoom-out-100 max-sm:data-[state=closed]:slide-out-to-bottom sm:max-w-3xl"
        showCloseButton={false}
        // During a tutorial the steps are anchored to elements inside this
        // dialog. Escape / outside-click would close it and strand the tour on a
        // missing target, so we swallow those dismiss gestures while a tutorial
        // is active. The tour has its own exit control in the overlay.
        onEscapeKeyDown={(event) => {
          if (tutorialActive) {
            event.preventDefault();
            return;
          }
          // Ladder through the @-mention menu first: step back a level or close
          // the menu, keeping the dialog open. Only once the menu is gone does
          // Escape fall through to dismiss the dialog.
          if (mentionEscapeRef.current?.()) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (tutorialActive || isWithinPortaledOverlay(event.detail.originalEvent.target)) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (tutorialActive || isWithinPortaledOverlay(event.detail.originalEvent.target)) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="shrink-0 flex-col items-stretch gap-2 space-y-0 px-4 pt-4 pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <div className="flex min-w-0 items-start gap-2 sm:min-w-0 sm:flex-1">
            <div className="min-w-0 flex-1">
              <DialogTitle className="flex items-center gap-2 text-base">
                {title ?? 'Launch Agent'}
                {!disablePersistence && open && (
                  <DraftAutosaveIndicator
                    status={saveStatus}
                    visible={Boolean(activeDraftId || isDirty || saveStatus === 'error')}
                    className="text-2xs font-normal text-muted-foreground"
                  />
                )}
                <InlineDocsHint slug="concepts/agents#starting-a-session" />
              </DialogTitle>
              <DialogDescription className="text-xs max-sm:hidden">
                {description ??
                  (activePersona
                    ? `Using persona "${activePersona.name}". Tweak anything below before launching.`
                    : 'Describe a task and configure how the agent should run.')}
              </DialogDescription>
            </div>
            {/* The full-height mobile sheet has no "outside" to tap and Escape
                needs a keyboard, so expose an explicit close affordance in the
                title row. Hidden during tutorials for the same reason Escape
                is swallowed. */}
            {!tutorialActive && (
              <DialogClose
                aria-label="Close"
                className="shrink-0 rounded-xs p-1 text-muted-foreground transition-colors hover:text-foreground sm:hidden"
              >
                <X className="h-4 w-4" />
              </DialogClose>
            )}
          </div>
          <LaunchPersonaHeader
            personas={personas}
            selectedPersonaId={selectedPersonaId}
            detailsOpen={detailsOpen}
            onPersonaChange={handlePersonaChange}
            onToggleDetails={() => setDetailsOpen((v) => !v)}
          />
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {baseBuilding.isBuilding ? (
            <div className="p-4">
              <BlueprintBuildingNotice status={baseBuilding.status!} />
            </div>
          ) : (
            <>
              <AnimatedCollapse open={detailsOpen && !!activePersona} className="px-4 pt-3">
                {shownPersona && (
                  <AnimatePresence initial={false} mode="popLayout">
                    <motion.div
                      key={shownPersona.id}
                      initial={{ opacity: 0, filter: 'blur(2px)' }}
                      animate={{ opacity: 1, filter: 'blur(0px)' }}
                      exit={{ opacity: 0, filter: 'blur(2px)' }}
                      transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                    >
                      <PersonaDetailCard persona={shownPersona} onClear={handleClearPersona} />
                    </motion.div>
                  </AnimatePresence>
                )}
              </AnimatedCollapse>

              <div className="p-4 pb-3">
                <AppliedContextBar
                  attachmentValues={attachmentValues}
                  activePersona={activePersona}
                  activeGroup={activeGroup}
                  onClearGroup={() =>
                    dispatch({ type: 'set_field', field: 'groupId', value: null })
                  }
                  leading={<AddContextButton onOpen={() => openMentionRef.current?.()} />}
                  // Tuck the "no blueprint for this repo" nudge inside the repo
                  // chip, just before its remove button. A picked snapshot is its
                  // own fast path, so skip it there; the nudge self-hides once a
                  // blueprint already bakes the repo in.
                  attachmentAccessories={
                    repoSlug && !snapshotId
                      ? {
                          [GIT_REPO_ATTACHMENT_ID]: (
                            <AgentRepoBlueprintWarning
                              repoSlug={repoSlug}
                              repoBranch={gitRepo?.repoBranch}
                              repoAccess={gitRepo?.repoAccess}
                            />
                          ),
                        }
                      : undefined
                  }
                  onClearPersona={handleClearPersona}
                  onRemoveAttachment={(id) =>
                    dispatch({
                      type: 'set_attachment',
                      id,
                      value: null,
                      pluginName: attachmentValues[id]?.pluginName ?? '',
                    })
                  }
                  // Lets a chip edit its own config in place — today the repo
                  // chip's branch, so switching branch is one click above the
                  // composer instead of a detour through the picker.
                  // The chip's own picker. Unlike `onChangeAttachment` this may
                  // legitimately write an attachment that is currently absent:
                  // deselecting the last MCP server clears the attachment, and
                  // picking another one from the still-open popover re-adds it.
                  onSetAttachment={(id, value, pluginName) =>
                    dispatch({ type: 'set_attachment', id, value, pluginName })
                  }
                  onChangeAttachment={(id, config) => {
                    // Guard: attachmentValues[id] may be undefined on a re-render
                    // race between the chip's edit commit and a state flush. An
                    // absent entry means the attachment was concurrently removed;
                    // writing pluginName: '' would corrupt the record.
                    const current = attachmentValues[id];
                    if (!current) return;
                    dispatch({
                      type: 'set_attachment',
                      id,
                      value: config,
                      pluginName: current.pluginName,
                    });
                  }}
                />
                <ChatInput
                  key={chatInputKey}
                  // `startAgent` expands context markers in the prompt.
                  contextReferences
                  onSend={handleCreate}
                  onChange={handlePromptChange}
                  onAttachmentsChange={(attachments) =>
                    dispatch({ type: 'set_chat_attachments', attachments })
                  }
                  isPending={launchPending}
                  compact
                  autoFocus
                  submitLabel="Launch agent"
                  imagesSupported={agentTypeRegistry.modelSupportsImages(agentType, model)}
                  // Keep the prompt + attachments in the composer through submit
                  // so a failed launch (e.g. server error) leaves everything in
                  // place for a one-click retry instead of wiping the form. On
                  // success the dialog closes and resets on next open (which
                  // bumps chatInputKey to remount a fresh ChatInput).
                  preserveOnSubmit
                  initialValue={initialPrompt}
                  initialAttachments={chatAttachments}
                  onEscape={() => {
                    // The composer is autofocused, so Escape lands here (not
                    // Radix). During a tutorial the steps are anchored inside
                    // this dialog, so swallow Escape to keep the tour intact.
                    if (tutorialActive) return;
                    handleOpenChange(false);
                  }}
                  submitRef={submitRef}
                  mentionEscapeRef={mentionEscapeRef}
                  openMentionRef={openMentionRef}
                  leadingToolbar={
                    <>
                      <AgentSettingsChipContainer
                        compactOnMobile
                        agentType={agentType}
                        selectedProviderId={selectedProviderId}
                        model={model}
                        modelEndpointId={modelEndpointId}
                        reasoningEffort={reasoningEffort}
                        onAgentTypeChange={handleAgentTypeChange}
                        onModelChange={handleModelChange}
                        onReasoningEffortChange={(effort) =>
                          dispatch({ type: 'set_field', field: 'reasoningEffort', value: effort })
                        }
                        onEndpointChange={handleEndpointChange}
                        launchableEndpointsOverride={launchableEndpoints}
                        providerSecretId={selectedProviderSecretId}
                        onProviderSecretChange={(v) =>
                          dispatch({
                            type: 'set_field',
                            field: 'selectedProviderSecretId',
                            value: v,
                          })
                        }
                        showMissingKeyError={showMissingKeyError}
                      />
                      {planModeEnabled && (
                        <>
                          <Button
                            variant={promptMode === 'plan' ? 'secondary' : 'ghost'}
                            size="xs"
                            className="gap-1 text-xs"
                            title={
                              promptMode === 'plan'
                                ? 'Plan mode active - agent will plan before executing'
                                : 'Enable plan mode'
                            }
                            onClick={() =>
                              dispatch({
                                type: 'set_field',
                                field: 'promptMode',
                                value: promptMode === 'plan' ? null : 'plan',
                              })
                            }
                          >
                            <ClipboardList className="icon-sm" />
                            Plan
                          </Button>
                          <Button
                            variant={promptMode === 'review' ? 'secondary' : 'ghost'}
                            size="xs"
                            className="gap-1 text-xs"
                            title={
                              promptMode === 'review'
                                ? 'Review mode active - agent will only read and analyze code'
                                : 'Enable review mode'
                            }
                            onClick={() =>
                              dispatch({
                                type: 'set_field',
                                field: 'promptMode',
                                value: promptMode === 'review' ? null : 'review',
                              })
                            }
                          >
                            <Eye className="icon-sm" />
                            Review
                          </Button>
                        </>
                      )}
                    </>
                  }
                  renderMentionMenu={({
                    query,
                    style,
                    onClose,
                    clearMentionToken,
                    insertMention,
                    resetMentionQuery,
                    setMentionQuery,
                    reportResults,
                    menuRef,
                  }) => (
                    <LaunchMentionMenu
                      ref={menuRef}
                      query={query}
                      style={style}
                      attachments={attachmentValues}
                      repoSlug={gitRepo?.repoSlug}
                      selectedPersonaId={selectedPersonaId}
                      onResetQuery={resetMentionQuery}
                      onScopeCategory={(keyword) => setMentionQuery(`${keyword}:`)}
                      onResultsChange={reportResults}
                      onApply={(result) => {
                        const action = handleMentionApply(result);
                        if (action.type === 'reference-agent-run') {
                          // Insert an inline, atomic chip at the @query.
                          insertMention({
                            kind: 'agent-run',
                            id: action.agentId,
                            title: action.title,
                          });
                        } else if (action.type === 'reference-context') {
                          insertMention({
                            kind: 'context',
                            contextKind: action.contextKind,
                            id: action.id,
                            title: action.title,
                          });
                        } else {
                          // Persona/attachment mentions configure the run; drop
                          // the @query text.
                          clearMentionToken();
                        }
                        onClose();
                      }}
                      onClose={onClose}
                      onRequestCreate={(request) => {
                        // Drop the `@query` text (the run is being configured,
                        // not written to) and close the menu, then open the
                        // plugin's create modal at this stable host level.
                        clearMentionToken();
                        onClose();
                        setMentionCreate(request);
                      }}
                      onRequestEdit={(request) => {
                        // Same handoff as create: the menu is about to unmount,
                        // so the plugin's edit modal is mounted below instead.
                        clearMentionToken();
                        onClose();
                        setMentionEdit(request);
                      }}
                    />
                  )}
                />
              </div>

              <div className="mx-4 mb-4 space-y-4">
                {configMounted && (
                  <AgentConfigFormSection
                    key={configSectionKey}
                    hideSettingsChip
                    hideAttachments
                    advancedStartsCollapsed
                    agentType={agentType}
                    selectedProviderId={selectedProviderId}
                    model={model}
                    modelEndpointId={modelEndpointId}
                    reasoningEffort={reasoningEffort}
                    blueprintName={selectedBlueprintName}
                    resourceSize={resourceSize}
                    customSize={customSize}
                    customSizes={orgCustomSizes}
                    defaultSandboxSize={orgDefaultSandboxSize}
                    isTrial={isTrial}
                    dockerd={dockerd}
                    computerUse={computerUse}
                    idleTimeMinutes={idleTimeMinutes}
                    attachmentValues={attachmentValues}
                    envVars={envVars}
                    hasStoredSecrets={Boolean(activePersona)}
                    onAgentTypeChange={handleAgentTypeChange}
                    onModelChange={handleModelChange}
                    onReasoningEffortChange={(effort) =>
                      dispatch({ type: 'set_field', field: 'reasoningEffort', value: effort })
                    }
                    onEndpointChange={handleEndpointChange}
                    onBlueprintChange={(name) =>
                      dispatch({
                        type: 'set_field',
                        field: 'manualBlueprintName',
                        value: name,
                      })
                    }
                    snapshots={snapshots}
                    snapshotId={snapshotId}
                    onSnapshotChange={(id) =>
                      dispatch({ type: 'set_field', field: 'snapshotId', value: id })
                    }
                    onResourceSizeChange={(size) =>
                      dispatch({ type: 'set_field', field: 'resourceSize', value: size })
                    }
                    onCustomSizeChange={(size) =>
                      dispatch({ type: 'set_field', field: 'customSize', value: size })
                    }
                    onDockerdChange={(enabled) =>
                      dispatch({ type: 'set_field', field: 'dockerd', value: enabled })
                    }
                    onComputerUseChange={
                      computerUseAvailable
                        ? (enabled) =>
                            dispatch({ type: 'set_field', field: 'computerUse', value: enabled })
                        : undefined
                    }
                    onIdleTimeMinutesChange={(minutes) =>
                      dispatch({ type: 'set_field', field: 'idleTimeMinutes', value: minutes })
                    }
                    onAttachmentChange={(id, value, pluginName) =>
                      dispatch({ type: 'set_attachment', id, value, pluginName })
                    }
                    onEnvVarsChange={(next) => dispatch({ type: 'set_env_vars', envVars: next })}
                    labelIds={labelIds}
                    onLabelIdsChange={(next) =>
                      dispatch({ type: 'set_field', field: 'labelIds', value: next })
                    }
                    groupId={groupId}
                    onGroupIdChange={(next) =>
                      dispatch({ type: 'set_field', field: 'groupId', value: next })
                    }
                    launchableEndpointsOverride={launchableEndpoints}
                    providerSecretId={selectedProviderSecretId}
                    onProviderSecretChange={(v) =>
                      dispatch({ type: 'set_field', field: 'selectedProviderSecretId', value: v })
                    }
                  />
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-row items-center justify-end gap-2 border-t border-border bg-muted/30 px-4 py-3 max-sm:pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
          <div className="mr-auto flex items-center gap-2 text-2xs text-muted-foreground">
            {baseBuilding.isBuilding ? (
              <span>Waiting for Blueprint&hellip;</span>
            ) : (
              <>
                <KbdCombo
                  keys={['enter']}
                  size="sm"
                  keyClassName="border-border/80 bg-gradient-to-b from-muted/60 to-muted font-semibold text-foreground shadow-sm"
                />
                <span>to launch</span>
              </>
            )}
          </div>
          {/* An org that allows no sharing at all has no share affordance:
            the launcher offers nothing the server would accept. */}
          {!baseBuilding.isBuilding &&
            activeOrganizationId &&
            !isSessionSharingDisabled(sharingPolicy) && (
              <LaunchShareMenu
                value={shareSelections}
                onChange={handleSharesChange}
                teams={myTeams}
                users={shareableUsers}
                orgId={activeOrganizationId}
                orgName={activeOrganization?.name}
                policy={sharingPolicy}
                disabled={launching || startAgent.isPending}
              />
            )}
          {!baseBuilding.isBuilding && !disablePersistence && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => void clearForm()}
              title="Clear all form values and delete the draft"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Clear
            </Button>
          )}
          {/* Cancel abandons the launch, not the draft — clearing the draft,
              wiping persisted config, and resetting the form is the explicit
              job of "Clear" — so Cancel closes like any other dismissal. */}
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => handleOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs"
            disabled={cannotLaunch}
            aria-busy={launchPending}
            onClick={() => submitRef.current?.()}
            // Tutorial hook: the launch tour spotlights this footer button. The
            // composer's send icon button carries aria-label="Launch agent",
            // so the tour can't key off that label without landing on the wrong
            // control — it targets this id instead.
            data-tutorial-id="launch-submit"
          >
            <span className="relative inline-flex size-3.5 items-center justify-center">
              <AnimatePresence initial={false} mode="popLayout">
                <motion.span
                  key={launchPending ? 'spinner' : 'rocket'}
                  className="absolute inset-0 inline-flex items-center justify-center"
                  initial={{ opacity: 0, filter: 'blur(2px)' }}
                  animate={{ opacity: 1, filter: 'blur(0px)' }}
                  exit={{ opacity: 0, filter: 'blur(2px)' }}
                  transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                >
                  {launchPending ? <Spinner size="sm" /> : <Rocket className="size-3.5" />}
                </motion.span>
              </AnimatePresence>
            </span>
            Launch
          </Button>
        </DialogFooter>

        {/*
          Inline "Add new …" create surface requested from the `@`-mention menu.
          The plugin component owns its own modal; on success it dispatches a
          `LaunchMentionResult` we apply to the run, then we unmount it.
        */}
        {mentionCreate && (
          <mentionCreate.CreateComponent
            attachments={attachmentValues}
            selectedPersonaId={selectedPersonaId}
            defaultName={mentionCreate.defaultName}
            onApply={(result) => {
              handleMentionApply(result);
              setMentionCreate(null);
            }}
            onClose={() => setMentionCreate(null)}
          />
        )}

        {/*
          Inline "Edit …" surface requested from a mention row's cog. Saving
          both persists the edit and applies the item to the run — reaching the
          cog means the user was picking that row.
        */}
        {mentionEdit && (
          <mentionEdit.EditComponent
            itemId={mentionEdit.itemId}
            attachments={attachmentValues}
            selectedPersonaId={selectedPersonaId}
            onApply={(result) => {
              handleMentionApply(result);
              setMentionEdit(null);
            }}
            onClose={() => setMentionEdit(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
